import {useState} from "react";

import {Button} from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {Label} from "@/components/ui/label";
import {Textarea} from "@/components/ui/textarea";
import {useToast} from "@/src/modules/core/hooks";
import {PropertiesCombobox} from "@/src/modules/properties/components/properties-combobox2";
import type {Property} from "@/src/modules/properties/types";
import {useAddPatrolVisit} from "@/src/modules/shifts/hooks/use-add-patrol-visit";

type AddPatrolStopDialogProps = {
  open: boolean;
  shiftId: number;
  onClose: () => void;
};

export function AddPatrolStopDialog({open, shiftId, onClose}: AddPatrolStopDialogProps) {
  const toast = useToast();
  const {mutateAsync, isPending} = useAddPatrolVisit();
  const [property, setProperty] = useState<Property | null>(null);
  const [duties, setDuties] = useState("");

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    setProperty(null);
    setDuties("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!property) {
      toast.error("Select a property");
      return;
    }

    try {
      await mutateAsync({
        shiftId,
        data: {
          property_id: property.id,
          duties: duties.trim() ? duties.trim() : null,
        },
      });
      toast.success("Stop added");
      handleOpenChange(false);
    } catch {
      // errorHandler
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add stop</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>
              Property <span className="text-destructive">*</span>
            </Label>
            <PropertiesCombobox
              shouldFilterByUserProperties
              value={property}
              onChange={setProperty}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="patrol-stop-duties">Duties (optional)</Label>
            <Textarea
              id="patrol-stop-duties"
              value={duties}
              onChange={(e) => setDuties(e.target.value)}
              rows={3}
              placeholder="What to do at this stop"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={isPending || !property} onClick={handleSubmit}>
            {isPending ? "Adding…" : "Add stop"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
